import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Laptop, Trees, Waves, Tag, Lock, MapPin } from 'lucide-react'
import { AnimatedSection } from '@/components/shared/AnimatedSection'
import { ROOMS_ROUTE, SITE_NAME } from '@/lib/constants'
import { getImageUrl } from '@/lib/image-url'

const AMENITIES = [
  {
    icon: Laptop,
    titleEs: 'Áreas de Trabajo Privadas',
    titleEn: 'Private Work Areas',
    descEs: 'Cada habitación cuenta con área de trabajo privada equipada para la máxima productividad.',
    descEn: 'Every room includes a dedicated private work area for maximum productivity.',
  },
  {
    icon: Trees,
    titleEs: 'Jardines Interiores Privados',
    titleEn: 'Private Interior Gardens',
    descEs: 'Nuestros jardines interiores brindan un oasis de paz para relajarse entre reuniones.',
    descEn: 'Our interior gardens offer a peaceful oasis to recharge between meetings.',
  },
  {
    icon: Waves,
    titleEs: 'Piscina',
    titleEn: 'Pool',
    descEs: 'Sumérjase en un oasis de tranquilidad. Nuestra piscina es el lugar ideal para desconectar.',
    descEn: 'Immerse yourself in tranquility. Our pool is the ideal place to unwind.',
  },
  {
    icon: Tag,
    titleEs: 'Mejor Precio Garantizado',
    titleEn: 'Best Price Guaranteed',
    descEs: 'Reserve directamente con nosotros y obtenga siempre el mejor precio disponible.',
    descEn: 'Book directly with us and always get the best available rate.',
  },
  {
    icon: Lock,
    titleEs: 'Pago Seguro',
    titleEn: 'Secure Payment',
    descEs: 'Transacciones seguras y protegidas para su tranquilidad en cada reserva.',
    descEn: 'Safe and protected transactions for your peace of mind on every booking.',
  },
  {
    icon: MapPin,
    titleEs: 'Mejor Ubicación',
    titleEn: 'Best Location',
    descEs: 'En el corazón histórico de Comayagua, a pasos de la Catedral y el Parque Central.',
    descEn: 'In the historic heart of Comayagua, steps from the Cathedral and Central Park.',
  },
]

export function AboutPage() {
  const { i18n } = useTranslation()
  const es = !i18n.language.startsWith('en')

  return (
    <>
      <Helmet>
        <title>{`${es ? 'Sobre Nosotros' : 'About Us'} | ${SITE_NAME}`}</title>
        <meta
          name="description"
          content={
            es
              ? 'Conozca la historia y el equipo detrás de Hotel Boutique Nueva Valladolid. Un refugio corporativo con arquitectura colonial en el corazón histórico de Comayagua, Honduras.'
              : 'Discover the story and team behind Hotel Boutique Nueva Valladolid. A corporate refuge with colonial architecture in the historic heart of Comayagua, Honduras.'
          }
        />
      </Helmet>

      {/* ── HERO PARALLAX ── */}
      <section
        className="relative flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `url(${getImageUrl('/images/nuva/hero/hero-habitaciones-page.jpeg')})`,
          backgroundAttachment: 'fixed',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          paddingTop: '18rem',
          paddingBottom: '10rem',
        }}
      >
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative z-10 px-4 text-center">
          <h1
            className="text-6xl font-normal text-white"
            style={{ fontFamily: 'VeniceBridge, serif', textShadow: '0 0 15px rgba(0,0,0,0.8)' }}
          >
            {es ? 'Sobre Nosotros' : 'About Us'}
          </h1>
          <h2
            className="mt-2 text-3xl font-bold uppercase tracking-wide text-[#F3EADB]"
            style={{ marginTop: '-8px' }}
          >
            {SITE_NAME}
          </h2>
        </div>
      </section>

      {/* ── MISSION / ABOUT TEXT ── */}
      <section className="bg-white py-24 px-10">
        <div className="mx-auto max-w-7xl">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold uppercase tracking-wide text-[#0F2966] mb-4">
                {es ? 'Hotel Boutique Nueva Valladolid' : 'Hotel Boutique Nueva Valladolid'}
              </h2>
              <p className="text-xl text-[#666666] italic max-w-3xl mx-auto">
                {es
                  ? 'Comprometidos con la Comodidad y Productividad Corporativa'
                  : 'Committed to Corporate Comfort and Productivity'}
              </p>
            </div>
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-base text-[#666666] leading-relaxed mb-6">
                {es
                  ? 'Ubicado en el centro histórico de Comayagua, nuestro hotel está diseñado para ofrecer un entorno sereno y profesional, ideal para viajeros de negocios. Con habitaciones silenciosas, áreas de trabajo privadas y espacios para reuniones totalmente equipados, brindamos el equilibrio perfecto entre confort y funcionalidad.'
                  : 'Located in the historic center of Comayagua, our hotel is designed to offer a serene and professional environment, ideal for business travelers. With quiet rooms, private work areas, and fully equipped meeting spaces, we provide the perfect balance between comfort and functionality.'}
              </p>
              <p className="text-base text-[#666666] leading-relaxed mb-6">
                {es
                  ? 'Los bellos jardines interiores, la piscina y la atención personalizada garantizan una experiencia relajante, lejos del bullicio, permitiendo a nuestros huéspedes concentrarse en sus proyectos y disfrutar del encanto colonial de la ciudad.'
                  : 'Our beautiful interior gardens, pool, and personalized attention ensure a relaxing experience away from the bustle, allowing guests to focus on their projects and enjoy the colonial charm of the city.'}
              </p>
              <p className="text-base text-[#666666] leading-relaxed">
                {es
                  ? 'Desde el Hotel Boutique Nueva Valladolid, todo el patrimonio colonial de Comayagua está a pocos minutos a pie. Somos el punto de encuentro ideal para ejecutivos, equipos en retiro y visitantes de negocios que buscan comodidad sin renunciar al entorno arquitectónico que distingue a esta ciudad.'
                  : 'From Hotel Boutique Nueva Valladolid, all of Comayagua\'s colonial heritage is just a few minutes\' walk away. We are the ideal meeting point for executives, retreat teams, and business visitors seeking comfort within the unique architectural setting that defines this city.'}
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ── WHY CHOOSE US — 6 FEATURE ICONS ── */}
      <section className="bg-white py-24 px-10 border-t border-[#EEE7E0]">
        <div className="mx-auto max-w-7xl">
          <AnimatedSection>
            <h2 className="text-center text-3xl font-bold uppercase tracking-wide text-[#0F2966] mb-16">
              {es
                ? 'Por qué elegir Hotel Boutique Nueva Valladolid'
                : 'Why Choose Hotel Boutique Nueva Valladolid'}
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {AMENITIES.map((item, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <div className="flex flex-col items-center text-center px-4">
                  <div
                    className="mb-5 flex items-center justify-center rounded-full"
                    style={{
                      width: '60px',
                      height: '60px',
                      border: '1px solid #0F2966',
                      flexShrink: 0,
                    }}
                  >
                    <item.icon className="h-6 w-6 text-[#0F2966]" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-2xl font-bold uppercase tracking-wide text-[#0F2966] mb-3">
                    {es ? item.titleEs : item.titleEn}
                  </h3>
                  <p className="text-sm text-[#666666] leading-relaxed max-w-xs">
                    {es ? item.descEs : item.descEn}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── AMENITIES DETAIL — 4 CARDS ── */}
      <section className="bg-[#0F2966] py-24 px-10">
        <div className="mx-auto max-w-7xl">
          <AnimatedSection>
            <h2 className="text-center text-3xl font-bold uppercase tracking-wide text-white mb-16">
              {es ? 'Nuestras Instalaciones' : 'Our Facilities'}
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {[
              {
                titleEs: 'Espacios de Trabajo Privado',
                titleEn: 'Private Work Spaces',
                bodyEs: 'Cada habitación incluye un espacio de trabajo privado, diseñado para garantizar concentración y comodidad, perfecto para avanzar en sus proyectos en un entorno tranquilo.',
                bodyEn: 'Each room includes a private workspace designed to ensure focus and comfort, perfect for advancing your projects in a quiet environment.',
                img: '/images/nuva/amenities/amenity-private-work-pod.jpeg',
                altEs: 'Área de trabajo privada',
                altEn: 'Private work area',
              },
              {
                titleEs: 'Jardines Interiores',
                titleEn: 'Interior Gardens',
                bodyEs: 'Nuestros hermosos jardines interiores brindan un oasis de paz, donde podrá relajarse y desconectar después de un día de trabajo, disfrutando de la serenidad y el encanto natural en pleno corazón de Comayagua.',
                bodyEn: 'Our beautiful interior gardens provide a peaceful oasis where you can relax and disconnect after a day of work, enjoying serenity and natural charm in the heart of Comayagua.',
                img: '/images/nuva/amenities/amenity-desk-workspace.jpeg',
                altEs: 'Jardines interiores del hotel',
                altEn: 'Hotel interior gardens',
              },
              {
                titleEs: 'Piscina',
                titleEn: 'Pool',
                bodyEs: 'Sumérjase en un oasis de tranquilidad. Nuestra piscina ofrece un ambiente sofisticado donde el confort y la serenidad se encuentran. Ideal para desconectar y disfrutar del momento.',
                bodyEn: 'Immerse yourself in an oasis of tranquility. Our pool offers a sophisticated atmosphere where comfort and serenity meet. Ideal for unwinding and enjoying the moment.',
                img: '/images/nuva/rooms/room-junior-suite-king-gallery-1.jpeg',
                altEs: 'Piscina del hotel',
                altEn: 'Hotel pool',
              },
              {
                titleEs: 'Restaurante',
                titleEn: 'Restaurant',
                bodyEs: 'Ofrecemos una experiencia sensorial única. Sabores exquisitos, presentaciones impecables y un ambiente íntimo que se combinan para crear momentos inolvidables.',
                bodyEn: 'We offer a unique sensory experience. Exquisite flavors, impeccable presentations, and an intimate atmosphere combine to create unforgettable moments.',
                img: '/images/nuva/rooms/room-sencilla-queen-gallery-1.jpeg',
                altEs: 'Restaurante del hotel',
                altEn: 'Hotel restaurant',
              },
            ].map((card, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="bg-white overflow-hidden shadow-sm">
                  <div
                    className="w-full"
                    style={{
                      backgroundImage: `url(${getImageUrl(card.img)})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      height: '220px',
                      borderRadius: '300px 300px 0 0',
                    }}
                    role="img"
                    aria-label={es ? card.altEs : card.altEn}
                  />
                  <div className="p-8">
                    <h3 className="text-2xl font-bold uppercase tracking-wide text-[#0F2966] mb-4">
                      {es ? card.titleEs : card.titleEn}
                    </h3>
                    <p className="text-base text-[#666666] leading-relaxed">
                      {es ? card.bodyEs : card.bodyEn}
                    </p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── TEAM SECTION ── */}
      <section className="bg-white py-24 px-10">
        <div className="mx-auto max-w-7xl">
          <AnimatedSection>
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold uppercase tracking-wide text-[#0F2966] mb-4">
                {es ? 'Nuestro Equipo' : 'Our Team'}
              </h2>
              <p className="text-base text-[#666666] leading-relaxed max-w-2xl mx-auto">
                {es
                  ? 'Detrás de cada estancia memorable hay un equipo comprometido que conoce cada rincón de Comayagua y está siempre dispuesto a facilitar lo que el huésped de negocios necesita.'
                  : 'Behind every memorable stay is a committed team that knows every corner of Comayagua and is always ready to support what business guests need.'}
              </p>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                nameEs: 'Recepción',
                nameEn: 'Front Desk',
                roleEs: 'Atención 24 horas',
                roleEn: '24-Hour Service',
                bioEs: 'Nuestro equipo de recepción está disponible las 24 horas del día, los 365 días del año para atender todas sus necesidades.',
                bioEn: 'Our front desk team is available 24 hours a day, 365 days a year to attend to all your needs.',
              },
              {
                nameEs: 'Administración',
                nameEn: 'Management',
                roleEs: 'Operaciones del hotel',
                roleEn: 'Hotel Operations',
                bioEs: 'Con años de experiencia en hospitalidad corporativa, nuestro equipo de administración garantiza operaciones impecables.',
                bioEn: 'With years of experience in corporate hospitality, our management team ensures flawless operations.',
              },
              {
                nameEs: 'Cocina',
                nameEn: 'Kitchen',
                roleEs: 'Gastronomía hondureña',
                roleEn: 'Honduran Gastronomy',
                bioEs: 'Nuestros chefs preparan cada platillo con ingredientes frescos y locales, ofreciendo una experiencia culinaria auténtica.',
                bioEn: 'Our chefs prepare each dish with fresh local ingredients, offering an authentic culinary experience.',
              },
              {
                nameEs: 'Mantenimiento',
                nameEn: 'Maintenance',
                roleEs: 'Instalaciones perfectas',
                roleEn: 'Perfect Facilities',
                bioEs: 'Nuestro equipo de mantenimiento asegura que todas las instalaciones del hotel estén siempre en condiciones perfectas.',
                bioEn: 'Our maintenance team ensures all hotel facilities are always in perfect condition.',
              },
            ].map((member, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="text-center">
                  <div
                    className="mx-auto mb-5 overflow-hidden bg-[#F3EADB]"
                    style={{
                      width: '100%',
                      aspectRatio: '4/3',
                      borderRadius: '300px 300px 0 0',
                      backgroundImage: `url(${getImageUrl('/images/nuva/staff/staff-placeholder.jpg')})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center top',
                    }}
                    role="img"
                    aria-label={es ? member.nameEs : member.nameEn}
                  />
                  <h3 className="text-xl font-bold uppercase tracking-wide text-[#0F2966] mb-1">
                    {es ? member.nameEs : member.nameEn}
                  </h3>
                  <p className="text-sm font-semibold text-[#61988E] mb-3">
                    {es ? member.roleEs : member.roleEn}
                  </p>
                  <p className="text-sm text-[#666666] leading-relaxed">
                    {es ? member.bioEs : member.bioEn}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── INSTAGRAM CTA ── */}
      <section className="bg-white py-16 px-10 border-t border-[#EEE7E0]">
        <div className="mx-auto max-w-7xl text-center">
          <AnimatedSection>
            <p className="text-sm font-bold uppercase tracking-widest text-[#0F2966] mb-2">
              {es ? 'Síguenos en Instagram' : 'Follow Us on Instagram'}
            </p>
            <h2 className="text-3xl font-bold uppercase tracking-wide text-[#0F2966] mb-4">
              Hotel Boutique Nueva Valladolid
            </h2>
            <a
              href="https://www.instagram.com/hotelboutiquenuevavalladolid"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-full border border-[#0F2966] bg-transparent text-[#0F2966] text-sm font-bold uppercase tracking-wide hover:bg-[#F3EADB] transition-colors"
              style={{ width: '175px', height: '50px' }}
            >
              @hbnuevavalladolid
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="bg-[#0F2966] py-24 px-10">
        <div className="mx-auto max-w-7xl text-center">
          <AnimatedSection>
            <h2 className="text-3xl font-bold uppercase tracking-wide text-white mb-4 md:text-4xl">
              {es
                ? 'NUEVA VALLADOLID es tu Hotel perfecto en Comayagua'
                : 'NUEVA VALLADOLID is your perfect Hotel in Comayagua'}
            </h2>
            <p className="mx-auto mb-10 max-w-2xl text-base text-white/85 leading-relaxed">
              {es
                ? 'Arquitectura colonial, servicios pensados para el viajero corporativo y hospitalidad hondureña auténtica nos respaldan. Reserve su estancia y descubra por qué somos el refugio preferido en el corazón de Comayagua.'
                : 'Colonial architecture, services designed for the corporate traveler, and authentic Honduran hospitality back us up. Book your stay and discover why we are the preferred refuge in the heart of Comayagua.'}
            </p>
            <Link
              to={ROOMS_ROUTE}
              className="inline-flex items-center justify-center rounded-full border border-white bg-transparent text-white text-sm font-bold uppercase tracking-wide hover:bg-[#F3EADB] hover:text-[#0F2966] transition-colors"
              style={{ width: '200px', height: '50px' }}
            >
              {es ? 'Reservar Ahora' : 'Book Now'}
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </>
  )
}
